import { faCheck, faEraser } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSubmitGuard } from '../../hooks/useSubmitGuard';
import { useThemeColors } from '../../providers/ThemeProvider';
import { useTranslation } from '../../providers/TranslationProvider';
import { cn } from '../../utils/cn';
import { Button } from './Button';

export interface SignaturePadProps {
	/**
	 * Capture mode (the delivery workflow): the pad shows Clear and Confirm Signature, and
	 * Confirm hands the PNG data URL to this callback, which may upload it.
	 */
	onSignatureCapture?: (signatureData: string) => Promise<void> | void;
	/** Capture mode: a signature already captured, drawn on the pad. */
	existingSignature?: string | null;
	/**
	 * Controlled mode (Workd Forms), used when `onChange` is given: the current signature
	 * as a PNG data URL, or null when the pad is blank.
	 */
	value?: string | null;
	/**
	 * Controlled mode: called with the PNG data URL each time a stroke ends, and with null
	 * when the pad is cleared. There is no Confirm button.
	 */
	onChange?: (signatureData: string | null) => void;
	/** Controlled mode: called when a stroke ends or the pad is cleared. */
	onBlur?: () => void;
	/** Controlled mode: the question the pad answers, its accessible name. */
	label?: string;
	height?: number;
	/**
	 * When `true`, the canvas grays out and ignores pointer input, the
	 * Clear + Confirm buttons disable, and the existing signature (if
	 * any) is hidden. Used by the stop-execution workflow when the
	 * driver has explicitly toggled "Customer wasn't available" — the
	 * canvas remains in the layout so unchecking the toggle restores
	 * a normal capture flow without re-rendering the whole step.
	 */
	disabled?: boolean;
	// Injected by FormField in controlled mode: the id the error summary focuses, and the
	// error wiring.
	id?: string;
	'aria-invalid'?: boolean;
	'aria-describedby'?: string;
}

// PD-13445: the exported PNG is embedded in customer-facing artifacts
// (proof-of-delivery email + invoice PDF, and a form submission's signature),
// so the canvas must NOT follow the app theme — a driver signing in dark mode
// used to bake a near-black background with white ink into the document. The
// pad renders as fixed "paper": white background, dark ink, every theme.
const SIGNATURE_PAPER = '#ffffff';
const SIGNATURE_INK = '#1f2937';

/** What is painted on the canvas now, so a redraw happens only when it would differ. */
interface PaintedModel {
	src: string | null;
	width: number;
}

/**
 * A signature drawn with a finger, stylus or mouse, exported as a PNG on white paper.
 * Moved here from the delivery workflow (PD-16691) so Workd Forms can ask for a signature
 * with the same pad.
 *
 * Two ways to use it:
 * - Capture mode (`onSignatureCapture`): Clear and Confirm Signature buttons; Confirm
 *   hands over the image. The delivery workflow's behavior, unchanged.
 * - Controlled mode (`value` + `onChange`): the image is reported as each stroke ends and
 *   null on Clear, so a form holds it like any other answer.
 *
 * The canvas is `touch-none`, so drawing on a phone never scrolls the page. Drawing needs
 * a pointer; keyboard and screen-reader users reach the pad's name, its state (signed or
 * not) and the Clear button.
 */
export const SignaturePad = ({
	onSignatureCapture,
	existingSignature,
	value,
	onChange,
	onBlur,
	label,
	height = 200,
	disabled = false,
	id,
	'aria-invalid': ariaInvalid,
	'aria-describedby': ariaDescribedBy,
}: SignaturePadProps): React.JSX.Element => {
	const { t } = useTranslation();
	const colors = useThemeColors();
	const containerRef = useRef<HTMLDivElement>(null);
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const paintedRef = useRef<PaintedModel | null>(null);
	const [isDrawing, setIsDrawing] = useState(false);
	const [hasSignature, setHasSignature] = useState(false);
	const [canvasWidth, setCanvasWidth] = useState(300);
	const isControlled = onChange != null;
	const shown = isControlled ? (value ?? null) : (existingSignature ?? null);

	const getContext = useCallback(() => {
		const canvas = canvasRef.current;

		if (canvas == null) {
			return null;
		}

		const ctx = canvas.getContext('2d');

		if (ctx != null) {
			ctx.strokeStyle = SIGNATURE_INK;
			ctx.lineWidth = 2;
			ctx.lineCap = 'round';
			ctx.lineJoin = 'round';
		}

		return ctx;
	}, []);

	const clearCanvas = useCallback(() => {
		const canvas = canvasRef.current;
		const ctx = getContext();

		if (canvas != null && ctx != null) {
			ctx.fillStyle = SIGNATURE_PAPER;
			ctx.fillRect(0, 0, canvas.width, canvas.height);
			setHasSignature(false);
			paintedRef.current = { src: null, width: canvas.width };
		}
	}, [getContext]);

	useEffect(() => {
		const updateWidth = (): void => {
			if (containerRef.current != null) {
				const containerWidth = containerRef.current.offsetWidth - 16;
				setCanvasWidth(Math.max(containerWidth, 200));
			}
		};

		updateWidth();
		// The box itself can change width without the window doing so (a form's layout
		// preview switching to phone width, a half-width cell becoming full width).
		const observer =
			typeof ResizeObserver === 'undefined' || containerRef.current == null
				? null
				: new ResizeObserver(updateWidth);
		if (observer != null && containerRef.current != null) {
			observer.observe(containerRef.current);
		}
		window.addEventListener('resize', updateWidth);

		return (): void => {
			observer?.disconnect();
			window.removeEventListener('resize', updateWidth);
		};
	}, []);

	useEffect(() => {
		const canvas = canvasRef.current;
		// Already on the canvas (the stroke that produced this value was just drawn there):
		// repainting would only race the visitor's next stroke. A resize clears a canvas, so
		// a new width always repaints.
		if (
			canvas != null &&
			paintedRef.current != null &&
			paintedRef.current.src === shown &&
			paintedRef.current.width === canvas.width
		) {
			return;
		}

		if (shown != null) {
			const img = new Image();

			img.onload = (): void => {
				const current = canvasRef.current;
				const ctx = getContext();

				if (current != null && ctx != null) {
					ctx.fillStyle = SIGNATURE_PAPER;
					ctx.fillRect(0, 0, current.width, current.height);
					// At its own proportions: a signature drawn on a narrow pad (a phone held
					// upright) is not stretched to a wider one, where the next stroke would
					// send the stretched image. Shrunk to fit when the pad got smaller.
					const scale = Math.min(
						1,
						current.width / Math.max(img.width, 1),
						current.height / Math.max(img.height, 1),
					);
					const drawnWidth = img.width * scale;
					const drawnHeight = img.height * scale;
					ctx.drawImage(
						img,
						0,
						(current.height - drawnHeight) / 2,
						drawnWidth,
						drawnHeight,
					);
					setHasSignature(true);
					paintedRef.current = { src: shown, width: current.width };
				}
			};

			img.src = shown;
		} else {
			clearCanvas();
		}
	}, [shown, clearCanvas, canvasWidth, getContext]);

	const getCoordinates = (
		e:
			| React.MouseEvent<HTMLCanvasElement>
			| React.TouchEvent<HTMLCanvasElement>,
	): { x: number; y: number } | null => {
		const canvas = canvasRef.current;

		if (canvas == null) {
			return null;
		}

		const rect = canvas.getBoundingClientRect();
		const scaleX = canvas.width / rect.width;
		const scaleY = canvas.height / rect.height;

		if ('touches' in e) {
			const touch = e.touches[0];

			if (touch == null) {
				return null;
			}

			return {
				x: (touch.clientX - rect.left) * scaleX,
				y: (touch.clientY - rect.top) * scaleY,
			};
		}

		return {
			x: (e.clientX - rect.left) * scaleX,
			y: (e.clientY - rect.top) * scaleY,
		};
	};

	const startDrawing = (
		e:
			| React.MouseEvent<HTMLCanvasElement>
			| React.TouchEvent<HTMLCanvasElement>,
	): void => {
		if (disabled) {
			return;
		}
		const coords = getCoordinates(e);
		const ctx = getContext();

		if (coords == null || ctx == null) {
			return;
		}

		ctx.beginPath();
		ctx.moveTo(coords.x, coords.y);
		setIsDrawing(true);
	};

	const draw = (
		e:
			| React.MouseEvent<HTMLCanvasElement>
			| React.TouchEvent<HTMLCanvasElement>,
	): void => {
		if (!isDrawing || disabled) {
			return;
		}

		const coords = getCoordinates(e);
		const ctx = getContext();

		if (coords == null || ctx == null) {
			return;
		}

		ctx.lineTo(coords.x, coords.y);
		ctx.stroke();
		setHasSignature(true);
	};

	const stopDrawing = (): void => {
		const ctx = getContext();

		if (ctx != null) {
			ctx.closePath();
		}

		// Controlled mode reports the image as each stroke ends, so the form always holds
		// what is on the pad.
		const canvas = canvasRef.current;
		if (isControlled && isDrawing && hasSignature && canvas != null) {
			const signatureData = canvas.toDataURL('image/png');
			paintedRef.current = { src: signatureData, width: canvas.width };
			onChange(signatureData);
			onBlur?.();
		}

		setIsDrawing(false);
	};

	const handleClear = (): void => {
		clearCanvas();
		if (isControlled) {
			onChange(null);
			onBlur?.();
		}
	};

	// PD-15267 — capturing the signature uploads it, so the same repeated-tap
	// exposure as the rest of the workflow applies here.
	const { submitting, guard } = useSubmitGuard();

	const handleConfirm = (): void => {
		const canvas = canvasRef.current;

		if (canvas == null || !hasSignature || onSignatureCapture == null) {
			return;
		}

		const signatureData = canvas.toDataURL('image/png');
		void guard(() => onSignatureCapture(signatureData));
	};

	const canvasElement = (
		<canvas
			ref={canvasRef}
			width={canvasWidth}
			height={height}
			className={cn(
				'w-full touch-none rounded',
				disabled ? 'cursor-not-allowed' : 'cursor-crosshair',
			)}
			style={{ backgroundColor: SIGNATURE_PAPER }}
			// A drawing surface has no keyboard equivalent; screen readers are told what it
			// is and whether it holds a signature.
			role={isControlled ? 'img' : undefined}
			aria-label={
				isControlled
					? hasSignature
						? t('Signature pad, signed')
						: t('Signature pad, not signed yet')
					: undefined
			}
			onMouseDown={startDrawing}
			onMouseMove={draw}
			onMouseUp={stopDrawing}
			onMouseLeave={stopDrawing}
			onTouchStart={startDrawing}
			onTouchMove={draw}
			onTouchEnd={stopDrawing}
		/>
	);

	if (isControlled) {
		return (
			<div
				id={id}
				role="group"
				aria-label={label != null ? t(label) : t('Signature')}
				aria-invalid={ariaInvalid}
				aria-describedby={ariaDescribedBy}
				// Focusable so the error summary's link can move to the question.
				tabIndex={-1}
				className={cn('space-y-2 outline-none', disabled ? 'opacity-40' : '')}
			>
				<div
					ref={containerRef}
					className={cn(
						'w-full rounded-lg border-2 border-dashed p-2',
						ariaInvalid === true
							? colors.classes.borderDanger
							: colors.classes.borderDefault,
						disabled ? 'pointer-events-none' : '',
					)}
				>
					{canvasElement}
				</div>
				<div className="flex flex-wrap items-center justify-between gap-2">
					<p className={cn('text-sm', colors.classes.textSecondary)}>
						{t('Sign in the box with your finger, a stylus or the mouse')}
					</p>
					<Button
						variant="outline"
						size="sm"
						onClick={handleClear}
						disabled={disabled || !hasSignature}
					>
						<FontAwesomeIcon icon={faEraser} className="mr-2" />
						{t('Clear')}
					</Button>
				</div>
			</div>
		);
	}

	return (
		<div className={cn('space-y-4', disabled ? 'opacity-40' : '')}>
			<div
				ref={containerRef}
				className={cn(
					'border-2 border-dashed rounded-lg p-2 w-full',
					colors.classes.borderDefault,
					disabled ? 'pointer-events-none' : '',
				)}
			>
				{canvasElement}
			</div>

			<p className={cn('text-sm text-center', colors.classes.textSecondary)}>
				{t('Sign above using your finger or mouse')}
			</p>

			<div className="flex justify-center gap-3">
				<Button
					variant="outline"
					size="sm"
					onClick={handleClear}
					disabled={disabled}
				>
					<FontAwesomeIcon icon={faEraser} className="mr-2" />
					{t('Clear')}
				</Button>
				<Button
					variant="primary"
					size="sm"
					onClick={handleConfirm}
					disabled={!hasSignature || disabled || submitting}
					loading={submitting}
					loadingLabel={t('Saving signature — please wait…')}
				>
					<FontAwesomeIcon icon={faCheck} className="mr-2" />
					{t('Confirm Signature')}
				</Button>
			</div>
		</div>
	);
};

interface SignatureImageProps {
	/** A PNG data URL already checked to be one (isFormSignatureDataUrl). */
	src: string;
	/** What the image shows, for screen readers. */
	alt: string;
	className?: string;
}

/**
 * A captured signature shown on the same fixed white paper the pad draws on, in both
 * themes (PD-13445): the image itself has a white background, and on a dark page it would
 * otherwise read as a white box with no edge.
 */
export function SignatureImage({
	src,
	alt,
	className,
}: SignatureImageProps): React.JSX.Element {
	const colors = useThemeColors();
	return (
		<div
			className={cn(
				'inline-block max-w-full rounded border p-1',
				colors.classes.borderDefault,
				className,
			)}
			style={{ backgroundColor: SIGNATURE_PAPER }}
		>
			<img src={src} alt={alt} className="block h-auto max-h-40 max-w-full" />
		</div>
	);
}
